import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { createSyncActions } from './helpers';
import { EvalResponse } from './utils';
import { snackbarShowMessage } from '../redux/actions/snackbarActions';
import { RAMError } from './RAMError';

interface IAxiosActionType {
  REQUEST: string;
  SUCCESS: string;
  REJECTED: string;
}

interface IAxiosActionOptions extends AxiosRequestConfig {
  handledErrorCode?: string;
}

export const axiosAction: any = {
  getAll: async function (
    endpoint,
    ActionType: IAxiosActionType,
    dispatch,
    { meta, ...params }: any = {},
  ) {
    const syncAction = createSyncActions(ActionType);
    if (!meta?._disableRequestStatus) {
      dispatch(syncAction.request());
    }
    try {
      const result: any = await this.get(endpoint, ActionType, dispatch, {
        meta: { ...meta, _disableRequestStatus: true },
        params,
      });
      if (result?.data?.pagination?.page < result?.data?.pagination?.max) {
        for (
          let page = result.data.pagination?.page + 1;
          page <= result.data.pagination?.max;
          page++
        ) {
          this.get(endpoint, ActionType, dispatch, {
            meta: { ...meta, _disableRequestStatus: true },
            params: { ...params, page },
          }).catch(error => {
            return error;
          });
        }
      }
      return result;
    } catch (error) {
      dispatch(syncAction.rejected());
      return error.response;
    }
  },
  get: async function (
    endpoint,
    actionType: IAxiosActionType,
    dispatch,
    { meta, ...params }: any = {},
  ) {
    return new Promise(async (resolve, reject) => {
      const syncAction = createSyncActions(actionType);
      if (!meta?._disableRequestStatus) {
        dispatch(syncAction.request());
      }
      try {
        const result: any = await axios(endpoint, params);
        if (result) {
          EvalResponse(result);
          dispatch(
            syncAction.success({
              meta,
              data: result.data,
            }),
          );
        }
        resolve(result);
      } catch (error) {
        showError(error, dispatch);
        dispatch(syncAction.rejected());
        reject(error);
      }
    });
  },
  post: function (
    endpoint,
    actionType: IAxiosActionType,
    dispatch,
    body,
    options: IAxiosActionOptions = {},
  ) {
    return new Promise(async (resolve, reject) => {
      const syncAction = createSyncActions(actionType);
      const { handledErrorCode, ...requestOptions } = options;
      dispatch(syncAction.request());
      try {
        const result: any = await axios.post(endpoint, body, requestOptions);
        EvalResponse(result);
        dispatch(
          syncAction.success({
            data: result.data,
          }),
        );
        resolve(result);
      } catch (error) {
        !isHandledError(error, handledErrorCode) && showError(error, dispatch);
        dispatch(syncAction.rejected());
        reject(error);
      }
    });
  },
  put: async function (
    endpoint,
    actionType: IAxiosActionType,
    dispatch,
    body,
    options: IAxiosActionOptions = {},
  ) {
    return new Promise(async (resolve, reject) => {
      const syncAction = createSyncActions(actionType);
      const { handledErrorCode, ...requestOptions } = options;

      dispatch(syncAction.request());
      try {
        const result: any = await axios.put(endpoint, body, requestOptions);
        EvalResponse(result);
        dispatch(
          syncAction.success({
            data: result?.data,
          }),
        );
        resolve(result);
      } catch (error) {
        if (!isHandledError(error, handledErrorCode)) {
          if (error instanceof RAMError) {
            dispatch(
              snackbarShowMessage(error.message ? error.message : error.code),
            );
          } else {
            const message =
              error?.response?.data?.error_code || 'INTERNAL_ERROR_SERVER';

            !(
              message === 'INTERNAL_SERVER_ERROR' &&
              Array.isArray(error?.response?.data?.error_description)
            ) && dispatch(snackbarShowMessage(message));
          }
        }
        dispatch(syncAction.rejected());
        reject(error);
      }
    });
  },
  patch: async function (
    endpoint,
    actionType: IAxiosActionType,
    dispatch,
    body,
  ) {
    return new Promise(async (resolve, reject) => {
      const syncAction = createSyncActions(actionType);
      dispatch(syncAction.request());
      try {
        const result: any = await axios.patch(endpoint, body);
        EvalResponse(result);
        dispatch(
          syncAction.success({
            data: result.data,
          }),
        );
        resolve(result);
      } catch (error) {
        if (error instanceof RAMError) {
          dispatch(
            snackbarShowMessage(error.message ? error.message : error.code),
          );
        } else {
          const message =
            error?.response?.data?.error_code || 'INTERNAL_ERROR_SERVER';

          !(
            message === 'INTERNAL_SERVER_ERROR' &&
            Array.isArray(error?.response?.data?.error_description)
          ) && dispatch(snackbarShowMessage(message));
        }
        dispatch(syncAction.rejected({ error }));
        reject(error);
      }
    });
  },
  delete: async function (
    endpoint,
    actionType: IAxiosActionType,
    dispatch,
    body,
  ) {
    return new Promise(async (resolve, reject) => {
      const syncAction = createSyncActions(actionType);
      dispatch(syncAction.request());
      try {
        const result: any = await axios.delete(
          endpoint,
          body && { data: body },
        );
        EvalResponse(result);
        dispatch(
          syncAction.success({
            data: result.data,
          }),
        );
        resolve(result);
      } catch (error) {
        showError(error, dispatch);
        dispatch(syncAction.rejected());
        reject(error);
      }
    });
  },
};
function isHandledError(error: AxiosError, handledErrorCode?: string): boolean {
  return (
    handledErrorCode !== undefined &&
    error?.response?.data?.error_code === handledErrorCode
  );
}

function showError(error: any, dispatch: any) {
  if (error instanceof RAMError) {
    dispatch(snackbarShowMessage(error.message ? error.message : error.code));
  } else {
    const message = error?.response?.data?.error_description
      ? error?.response?.data?.error_description
      : error?.response?.data?.error_code || 'INTERNAL_ERROR_SERVER';
    dispatch(snackbarShowMessage(message));
  }
}
